import { Form } from 'informed';
import { arrayOf, bool, number, shape, string } from 'prop-types';
import React, { Fragment, Suspense, useMemo, useState } from 'react';

import { Price } from '../peregrine';
import { useProductFullDetail } from '../talons/ProductFullDetail/useProductFullDetail';

// import { useStyle } from '@magento/venia-ui/lib/classify';
import { useStyle } from '../lib/util/classify';
import CREATE_CART_MUTATION from '../queries/createCart.graphql';
import GET_CART_DETAILS_QUERY from '../queries/getCartDetails.graphql';
import Breadcrumbs from '../components/Breadcrumbs';
import Button from '../components/Button';
import {
    ADD_CONFIGURABLE_MUTATION,
    ADD_MULTIPLE_SIMPLE_MUTATION
} from '../components/ProductFullDetail/productFullDetail.gql';
import Carousel from '../components/ProductImageCarousel';
import Quantity from '../components/ProductQuantity';
import RichContent from '../components/RichContent';
import RichText from '../components/RichText';
import Sharer from '../components/Sharer';
import YDWidgets from '../components/YDWidgets';
import ConfiguratorSection from './configuratorSection';
import { ADD_CONFIGURATOR_MUTATION } from './productConfigurator.gql';
import defaultClasses from './productConfigurator.module.css';
import SaveConfiguration from './ShareIcons/SaveConfiguration';
// Temporarily disabled for build
// const Dialog = React.lazy(() => import('../components/Dialog'));
const Dialog = () => null;
//import ProductAttributes from '../components/ProductFullDetail/productAttributes';
// const RelatedProducts = React.lazy(() =>
//     import('../components/ProductFullDetail/relatedProducts')
// );
const RelatedProducts = () => null;

const HELP_ATTRIBUTES_TO_PRINT = [
    'yd_meet_instr_txt',
    'yd_mont_hand_txt',
    'yd_levertijd_txt',
    'yd_info_bedien_txt',
    'yd_info_kast_txt',
    'yd_info_kleur',
    'yd_info_mont_txt'
];

// by pass for the PageBuilder, which makes the html content to be sent
// with the html entities encoded
const decodeHtmlEntities = string => {
    const textArea = document.createElement('textarea');
    textArea.innerHTML = string;
    return textArea.value;
};

// Simple error boundary component
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        console.error('Configurator Error:', error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div style={{padding: '20px', border: '2px solid red', margin: '20px'}}>
                    <h2>Configurator Error</h2>
                    <p>{this.state.error && this.state.error.toString()}</p>
                    <button onClick={() => this.setState({ hasError: false, error: null })}>
                        Retry
                    </button>
                </div>
            );
        }

        return this.props.children;
    }
}

const ProductFullDetail = props => {
    // Store product in ref to preserve it across re-renders
    const productRef = React.useRef(props.product);
    
    // Update ref if new product provided
    if (props.product && props.product !== productRef.current) {
        productRef.current = props.product;
    }
    
    // Use product from ref if props.product is undefined
    const rawProduct = props.product || productRef.current;
    
    // Debug logging - safer version
    console.log('ProductFullDetail render:', {
        hasProps: !!props,
        hasProduct: !!props.product,
        hasProductRef: !!productRef.current,
        usingRef: !props.product && !!productRef.current
    });

    if (!rawProduct) {
        console.error('ProductFullDetail: product is missing from props and ref');
        return <div style={{padding: '20px', border: '2px solid red'}}>
            Error: No product data provided to configurator. 
            Check console for details.
        </div>;
    }

    // Normalize product structure to match expected format
    // Template may provide { price: {value, currency} } but code expects:
    // - prices: array with price_tiers 
    // - price_range: { minimum_price: { regular_price, final_price } }
    const product = {
        ...rawProduct,
        // Ensure prices array exists (for discount calculations)
        prices: rawProduct.prices || [],
        // Ensure price_range exists (for price display)
        price_range: rawProduct.price_range || {
            minimum_price: {
                regular_price: rawProduct.price || { value: 0, currency: 'EUR' },
                final_price: rawProduct.price || { value: 0, currency: 'EUR' }
            }
        }
    };

    console.log('Product normalized:', {
        id: product.id,
        name: product.name,
        sku: product.sku,
        hasConfiguratorData: !!product.configurator_data,
        hasPrices: !!product.prices,
        hasPriceRange: !!product.price_range,
        typename: product.__typename
    });

    const productType = product.__typename
        .replace(/Product$/, '')
        .toLowerCase();

    if (productType !== 'simple') {
        return (
            <span style={{ color: 'red', fontWeight: 'bold' }}>
                CONFIGURABLE PRODUCTS EXPECTED TO BE <code>SimpleProduct</code>,
                CURRENT TYPE IS <code>{product.__typename}</code>
            </span>
        );
    }

    // @TODO: remove not needed dependency ADD_CONFIGURABLE_MUTATION
    const talonProps = useProductFullDetail({
        addConfigurableProductToCartMutation: ADD_CONFIGURABLE_MUTATION,
        addSimpleProductToCartMutation: ADD_CONFIGURATOR_MUTATION,
        addMultipleSimpleProductToCartMutation: ADD_MULTIPLE_SIMPLE_MUTATION,
        createCartMutation: CREATE_CART_MUTATION,
        getCartDetailsQuery: GET_CART_DETAILS_QUERY,
        product
    });

    const {
        breadcrumbCategoryId,
        handleAddToCart,
        handleSetQuantity,
        isAddToCartDisabled,
        mediaGalleryEntries,
        productDetails,
        quantity,
        resetConfigurator
    } = talonProps;

    const classes = useStyle(defaultClasses, props.classes);

    const breadcrumbs = breadcrumbCategoryId ? (
        <Breadcrumbs
            categoryId={breadcrumbCategoryId}
            currentProduct={productDetails.name}
        />
    ) : null;

    const [price, setPrice] = useState(productDetails.price.value);

    const configurator_data = JSON.parse(product.configurator_data);

    if (!configurator_data) {
        return <p>{_('Failed to load product configurator data')}</p>;
    }

    const [advicedPrice, setAdvicedPrice] = useState(
        productDetails.price.value
    );

    const [isConfiguratorValid, setIsConfiguratorValid] = useState(false);

    const [configuredColors, setConfiguredColors] = useState(null);
    const [configuredCaseType, setConfiguredCaseType] = useState(null);
    const [configuredCaseModel, setConfiguredCaseModel] = useState(null);
    const [configuredWidth, setConfiguredWidth] = useState(null);
    const [configuredHeight, setConfiguredHeight] = useState(null);

    const warningsBlock = configurator_data.warnings
        ? Object.keys(configurator_data.warnings)
              .map(
                  key => key + ': ' + configurator_data.warnings[key].join('\n')
              )
              .join('\n')
        : null;

    const priceBlock = (
        <span className={classes.configuratorPrices}>
            <p className={classes.advicePrice}>{_('Calculated price')}</p>
            <p className={classes.productPrice}>
                {advicedPrice ? (
                    <span className={classes.priceWithoutDiscount}>
                        <Price
                            currencyCode={productDetails.price.currency}
                            value={advicedPrice}
                        />
                    </span>
                ) : null}
                <Price
                    currencyCode={productDetails.price.currency}
                    value={price}
                />
            </p>
        </span>
    );

    const addToCartBlock = (
        <section className={classes.sidebar}>
            {priceBlock}
            <div className={classes.qtyHolder}>
                <Quantity
                    initialValue={quantity}
                    onValueChange={handleSetQuantity}
                />
            </div>
            <div className={classes.cartActions}>
                <Button
                    priority="high"
                    onClick={handleAddToCart}
                    disabled={isAddToCartDisabled || !isConfiguratorValid}
                    /* disabled={isAddToCartDisabled} */
                    classes={classes.addToCartButton}
                >
                    {_('Add to Cart')}
                </Button>
            </div>
            <div className={classes.resetConfigurator}>
                <SaveConfiguration
                    product={product}
                    label={_('Save and configure another one')}
                    onSaveComplete={resetConfigurator}
                />
            </div>
        </section>
    );

    const configuratorSection = (
        <ConfiguratorSection
            product={product}
            onPriceChange={setPrice}
            onAdvicedPriceChange={setAdvicedPrice}
            priceBlock={priceBlock}
            addToCartBlock={addToCartBlock}
            setIsConfiguratorValid={setIsConfiguratorValid}
            onConfiguredColorChange={setConfiguredColors}
            onConfiguredCaseChange={setConfiguredCaseType}
            onConfiguredCaseModelChange={setConfiguredCaseModel}
            onConfiguredWidthChange={setConfiguredWidth}
            onConfiguredHeightChange={setConfiguredHeight}
        />
    );

    const [dialogContent, setDialogContent] = useState(null);
    const [dialogTitle, setDialogTitle] = useState(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const hideDialog = () => {
        setIsDialogOpen(false);
    };

    const showDialog = info => {
        setDialogContent(info.text);
        setDialogTitle(info.title);
        setIsDialogOpen(true);
    };

    const configuratorHelpBlock = useMemo(() => {
        return configurator_data.help
            ? Object.entries(configurator_data.help)
                  .filter(
                      ([code, { text }]) =>
                          HELP_ATTRIBUTES_TO_PRINT.includes(code) &&
                          text &&
                          text.trim().length
                  )
                  .map(([code, { title, text }]) => (
                      <div className={classes.section} key={code}>
                          <div className={classes.txttitle}>{title}</div>
                          <RichText content={text} />
                      </div>
                  ))
            : null;
    }, [configurator_data.help]);

    const className = configurator_data.lengthonly
        ? classes.rootLengthOnly
        : classes.root;

    const productDetailsDescriptionClean = decodeHtmlEntities(
        productDetails.description
    );

    return (
        <Fragment>
            <div className={classes.nestcontainer}>
                {breadcrumbs}
                <Form className={className}>
                    <div className={classes.productinfo}>
                        <h1 className={classes.productName}>
                            {productDetails.name}{' '}
                            <Sharer
                                url={product.canonical_url}
                                title={product.name}
                                text={product.name}
                                buttonTitle={_('Share')}
                                buttonText={_('')}
                            />
                        </h1>

                        <div className={classes.productImage}>
                            <section className={classes.imageCarousel}>
                                <Carousel
                                    images={mediaGalleryEntries}
                                    configuredProduct={{
                                        width: configuredWidth,
                                        height: configuredHeight,
                                        colors: configuredColors,
                                        caseType: configuredCaseType,
                                        caseModel: configuredCaseModel
                                    }}
                                />
                            </section>
                        </div>
                    </div>

                    {warningsBlock ? (
                        <pre className={classes.warnings}>{warningsBlock}</pre>
                    ) : null}

                    <section className={classes.configurator}>
                        {configuratorSection}
                    </section>
                </Form>
            </div>
            <div className={classes.mobileonly}>
                {configurator_data.help ? (
                    <div className={classes.shortButtons}>
                        <Button
                            priority="high"
                            onClick={e => {
                                e.preventDefault();
                                showDialog(
                                    configurator_data.help.yd_prod_info_txt
                                );
                            }}
                        >
                            {_('Contenido')}
                        </Button>
                        <Button
                            priority="high"
                            onClick={e => {
                                e.preventDefault();
                                showDialog(
                                    configurator_data.help.yd_mont_hand_txt
                                );
                            }}
                        >
                            {_('Montaje')}
                        </Button>
                        <Button
                            priority="high"
                            onClick={e => {
                                e.preventDefault();
                                showDialog(configurator_data.help.yd_bedi_txt);
                            }}
                        >
                            {_('Ficha técnica')}
                        </Button>
                    </div>
                ) : null}
            </div>
            {productDetails.short_description ||
            productDetails.description ||
            configuratorHelpBlock ? (
                <section className={classes.productdetails}>
                    {/*
    <hr />
      <Image
        alt={product.name}
        classes={{
          image: classes.currentImage,
          root: classes.imageContainer
        }}
        resource={product.image.url}
        width="120"
    /> */}
                    {productDetails.short_description ? (
                        <div className={classes.section}>
                            <RichText
                                content={productDetails.short_description}
                            />
                        </div>
                    ) : null}
                    {productDetails.description ? (
                        <div className={classes.section}>
                            <div className={classes.txttitle}>
                                {_('Product information')}
                            </div>
                            <RichText
                                content={productDetailsDescriptionClean}
                            />
                        </div>
                    ) : null}
                    {configuratorHelpBlock}
                </section>
            ) : null}
            <YDWidgets
                type="product"
                code={productType}
                position="add_to_cart"
                place="after"
            />

            <YDWidgets
                type="product"
                code={productType}
                position="description"
                place="before"
            />

            <YDWidgets
                type="product"
                code={productType}
                position="description"
                place="after"
            />
            <YDWidgets
                type="product"
                code={productType}
                position="attribute_list"
                place="before"
            />
            <YDWidgets
                type="product"
                code={productType}
                position="attribute_list"
                place="after"
            />
            <Suspense fallback={null}>
                <RelatedProducts
                    items={productDetails.related_products}
                    classes={classes}
                />
            </Suspense>
            <Suspense fallback={null}>
                <Dialog
                    isOpen={isDialogOpen}
                    onConfirm={hideDialog}
                    onCancel={hideDialog}
                    showCancelButton={false}
                    confirmText={_('Close')}
                    title={dialogTitle}
                    lockScroll={false}
                >
                    <RichContent html={dialogContent} />
                </Dialog>
            </Suspense>
        </Fragment>
    );
};

ProductFullDetail.propTypes = {
    classes: shape({
        cartActions: string,
        description: string,
        descriptionTitle: string,
        details: string,
        detailsTitle: string,
        imageCarousel: string,
        productName: string,
        productPrice: string,
        quantity: string,
        quantityTitle: string,
        root: string,
        title: string
    }),
    product: shape({
        __typename: string,
        id: number,
        sku: string.isRequired,
        price: shape({
            price_range: shape({
                minimim_price: shape({
                    regular_price: shape({
                        currency: string.isRequired,
                        value: number.isRequired
                    })
                }).isRequired
            }).isRequired
        }).isRequired,
        media_gallery_entries: arrayOf(
            shape({
                label: string,
                position: number,
                disabled: bool,
                file: string.isRequired
            })
        ),
        description: string
    }).isRequired
};

// Wrap with error boundary for production safety
const ProductFullDetailWithErrorBoundary = props => (
    <ErrorBoundary>
        <ProductFullDetail {...props} />
    </ErrorBoundary>
);

export default ProductFullDetailWithErrorBoundary;
